import { useTranslation } from "react-i18next";
import {
  addDays,
  avatarColorForId,
  fmtNoShort,
  getDaySchedule,
  initials,
  isPlanDateKey,
  slotToTime,
} from "../../service/reports/agreement-redesign/schedule-utils";

export function PlanScheduleModalHead({ title, subtitle, onClose, t }) {
  return (
    <div className="ra-modal-head">
      <div>
        <div className="ra-modal-title">{title}</div>
        {subtitle ? <div className="ra-modal-sub">{subtitle}</div> : null}
      </div>
      <button type="button" className="ra-btn ghost" onClick={onClose} aria-label={t("Close")}>
        ✕
      </button>
    </div>
  );
}

export default function PlanScheduleModalFoot({
  planSelection,
  weekStart,
  selectedEmployees = [],
  assignmentCount = 0,
  datesOnly = false,
  hasConflict,
  onClose,
  onSubmit,
  isSubmitting,
  canSubmit,
  submitLabel,
}) {
  const { t } = useTranslation();
  const days = planSelection?.days || [];
  const dayTimeLabels = days
    .map((day) => {
      const s = getDaySchedule(planSelection, day);
      if (!s) return null;
      return `${slotToTime(s.startSlot)}–${slotToTime(s.endSlot)}`;
    })
    .filter(Boolean);
  const uniqueTimes = [...new Set(dayTimeLabels)];
  const timeLabel =
    uniqueTimes.length === 0
      ? null
      : uniqueTimes.length === 1
        ? uniqueTimes[0].replace("–", " – ")
        : t("work_order.plan_modal.varied_times");

  const formatPlanDay = (dayKey) =>
    isPlanDateKey(dayKey)
      ? fmtNoShort(new Date(`${dayKey}T12:00:00`))
      : fmtNoShort(addDays(weekStart, dayKey));

  const firstDay = days.length ? formatPlanDay(days[0]) : null;
  const lastDay =
    days.length > 1 ? formatPlanDay(days[days.length - 1]) : null;
  const dateLabel =
    days.length === 0
      ? null
      : days.length === 1
        ? firstDay
        : `${firstDay} – ${lastDay}`;

  return (
    <div className="ra-modal-foot">
      <div className="ra-foot-summary flex-wrap">
        {selectedEmployees.slice(0, 4).map((employee) => (
          <span key={employee.id} className="ra-pill">
            <span
              className="ra-emp-avatar"
              style={{
                background: avatarColorForId(employee.id),
                width: 18,
                height: 18,
                fontSize: 9,
              }}
            >
              {initials(employee.displayTitle || employee.name)}
            </span>
            {employee.displayTitle || employee.name}
          </span>
        ))}
        {selectedEmployees.length > 4 ? (
          <span className="ra-pill muted">
            +{selectedEmployees.length - 4}
          </span>
        ) : null}
        {datesOnly ? (
          dateLabel ? (
            <span className="ra-pill ok">
              {t("work_order.plan_modal.planning_type_dates_only")} · {dateLabel}
              {days.length > 1
                ? ` · ${t("work_order.plan_modal.selected_days_count", { count: days.length })}`
                : ""}
            </span>
          ) : (
            <span className="ra-pill muted">
              {t("work_order.plan_modal.pick_days_hint")}
            </span>
          )
        ) : dateLabel && timeLabel ? (
          <span className={`ra-pill ${hasConflict ? "warn" : "ok"}`}>
            {dateLabel} · {timeLabel}
            {assignmentCount > 1
              ? ` · ${t("work_order.plan_modal.assignment_count", { count: assignmentCount })}`
              : ""}
            {hasConflict ? ` · ${t("Conflict")}` : ""}
          </span>
        ) : (
          <span className="ra-pill muted">{t("work_order.plan_modal.pick_time_hint")}</span>
        )}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className="ra-btn ghost" onClick={onClose} disabled={isSubmitting}>
          {t("Cancel")}
        </button>
        <button
          type="button"
          className="ra-btn primary"
          onClick={onSubmit}
          disabled={isSubmitting || !canSubmit}
          aria-busy={isSubmitting || undefined}
        >
          {isSubmitting ? (
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white"
                aria-hidden="true"
              />
              {t("work_order.plan_modal.saving")}
            </span>
          ) : (
            submitLabel || t("Save")
          )}
        </button>
      </div>
    </div>
  );
}
